import { useEffect, useMemo, useRef, useState } from "react";
import bellIcon from "../assets/icons/ds/icon-bell.svg?raw";
import uploadIcon from "../assets/icons/ds/icon-upload.svg?raw";
import uploadBtn from "../assets/icons/ds/icon-upload-btn.svg?raw";
import sidebarSimple from "../assets/icons/sidebar-simple.svg";
import {
  CircularProgress,
  CircularProgressIndicator,
  CircularProgressRange,
  CircularProgressTrack,
} from "../components/CircularProgress";
import { DsIcon } from "../components/DsIcon";
import { RfqStatus } from "../components/RfqStatus";
import { Thumb } from "../components/Thumb";
import { Truncate } from "../components/Truncate";
import { useAlerts } from "../components/AlertsProvider";
import { useShell } from "../components/ShellContext";
import { useOrgCatalog } from "../hooks/useOrgCatalog";
import { uploadMixedFiles, listJobs } from "../lib/jobApi";
import { DEFAULT_PARSE_PREFS } from "../lib/parseMode";
import { getPlatform } from "../lib/platform";
import {
  buildE2eHomeRfqs,
  e2eOnboardingFixture,
  isE2eMockMode,
} from "../lib/e2eFixture";
import { fetchDemoStepFile } from "../lib/demoFixture";
import { drawingPresence } from "../lib/onboarding";
import { HomeFirstRun } from "./HomeFirstRun";
import { useLanguage } from "../lib/i18n";
import { logger } from "../lib/logger";
import type { Page } from "../routes";
import { fetchRfqStats, fetchRfqs, type RfqRecord, type RfqStats } from "../lib/rfqApi";
import { rfqDisplayStatus } from "../lib/rfqEvidence";
import {
  homeAgeLabel,
  homeRowProgress,
  homeSentWhen,
  homeWaiting,
  isRfqStats,
  orgStatsFromRfqs,
  sentWonSeries,
  type HomeWaitItem,
  type SentWonPoint,
} from "../lib/homeDashboard";
import { flattenDemandLines, lineThumbUrl } from "../lib/rfqUnderstanding";
import { cx } from "../lib/cx";
import { Button, ListSearch } from "../components/ds";
import { dsControl } from "../components/ds/styles";
import { settingsPageStyle } from "./settingsChrome";

interface Props {
  onNavigate: (page: Page, id?: string | null, sub?: string | null) => void;
}

type UploadRow = {
  id: string;
  name: string;
  ext: string;
  size: number;
  state: "uploading" | "done" | "error";
  message: string;
  /** Share of the file's bytes sent, 0..1. At 1 the job is being created and parsed. */
  progress?: number;
};

const ACCEPT = ".step,.stp,.dxf,.dwg,.pdf";

const glass =
  "overflow-hidden rounded-ds-card bg-ds-glass font-ds text-ds-ink shadow-ds-card backdrop-blur-ds backdrop-saturate-[1.1]";
const thumbFrame =
  "inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-ds-glass-strong [&>span]:inline-flex [&>span]:size-full [&>span]:items-center [&>span]:justify-center [&>span]:overflow-hidden [&_img]:block [&_img]:max-h-8 [&_img]:max-w-8 [&_img]:object-contain [&_img[data-thumb=true]]:size-full [&_img[data-thumb=true]]:max-h-none [&_img[data-thumb=true]]:max-w-none [&_img[data-thumb=true]]:object-cover";

function copy(locale: string) {
  const tr = locale === "tr";
  return {
    today: tr ? "Bugün" : "Today",
    search: tr ? "RFQ, belge veya parça ara" : "Search RFQs, documents or parts",
    winRate: tr ? "Kazanma oranı" : "Win rate",
    decided: (won: number, total: number) =>
      tr
        ? `${won} kazanıldı, ${total} sonuçlanmış teklif (kazanılan + kaybedilen). Açık teklifler sayılmaz.`
        : `${won} won of ${total} decided quotes (won + lost). Open quotes are not counted.`,
    won: tr ? "Kazanıldı" : "Won",
    lost: tr ? "Kaybedildi" : "Lost",
    open: tr ? "Açık" : "Open",
    chartTitle: tr ? "Teklifler, son 4 hafta: gönderilen ve kazanılan" : "Quotes, last 4 weeks: sent vs. won",
    sent: tr ? "Gönderilen" : "Sent",
    chartTip: (label: string, sent: number, won: number) =>
      tr ? `${label}\n${sent} gönderildi, ${won} kazanıldı` : `${label}\n${sent} sent, ${won} won`,
    chartEmpty: tr ? "Bu aralıkta gönderilen teklif yok." : "No quotes were sent in this window.",
    dropTitle: tr ? "Çizimleri veya RFQ dosyalarını buraya bırakın" : "Drop drawings or RFQ files here",
    dropHint: tr
      ? "STEP, STP, DXF, DWG ve PDF. Önce teklif verilip verilemeyeceğini kontrol eder, eksikleri listeleriz."
      : "STEP, STP, DXF, DWG and PDF. We first check if it can be quoted and list what's missing.",
    sample: tr ? "Örnek dosyayı dene" : "Try a sample file",
    choose: tr ? "Dosya seç" : "Choose files",
    uploading: tr ? "Yükleniyor" : "Uploading",
    uploaded: tr ? "Yüklendi" : "Uploaded",
    processing: tr ? "İşleniyor" : "Processing",
    filesReceived: tr ? "Dosyalar alındı" : "Files received",
    couldNotRead: tr ? "okunamadı" : "couldn't be read",
    waiting: tr ? "Sizi bekleyenler" : "Waiting on you",
    byPriority: tr ? "Önceliğe göre" : "By priority",
    recent: tr ? "Son RFQ'lar" : "Recent RFQs",
    allRfqs: tr ? "Tüm RFQ'lar" : "All RFQs",
    nothing: tr ? "Henüz bekleyen bir şey yok" : "Nothing waiting yet",
    recentEmpty: tr ? "Henüz RFQ yok" : "No RFQs yet",
    notifications: tr ? "Bildirimler" : "Notifications",
    alertsEmpty: tr ? "Sizi bekleyen bir şey yok." : "Nothing is waiting on you.",
    missing: tr ? "Eksik" : "Missing",
    customer: tr ? "Müşteri" : "Customer",
    followUp: tr ? "Takip" : "Follow-up",
    margin: tr ? "Marj" : "Margin",
    match: tr ? "Eşle" : "Match",
    pasteReply: tr ? "Yanıtı yapıştır" : "Paste reply",
    followAction: tr ? "Takip et" : "Follow up",
    reviewMargin: tr ? "Marjı incele" : "Review margin",
    missingTitle: (n: number) =>
      tr
        ? `${n} BOM satırının stok eşleşmesi yok`
        : n === 1
          ? "1 BOM line has no stock match"
          : `${n} BOM lines have no stock match`,
    customerTitle: (no: string, questions: number | null) => {
      if (questions != null && questions > 0) {
        return tr
          ? `${no} müşteriyi bekliyor, ${questions} açık soru`
          : questions === 1
            ? `${no} is waiting for the customer, 1 open question`
            : `${no} is waiting for the customer, ${questions} open questions`;
      }
      return tr ? `${no} müşteriyi bekliyor` : `${no} is waiting for the customer`;
    },
    followTitle: (no: string, days: number) =>
      tr
        ? `${no} için ${days} gündür yanıt yok`
        : days === 1
          ? `No reply to ${no} for 1 day`
          : `No reply to ${no} for ${days} days`,
    marginTitle: (pct: number) =>
      tr
        ? `Ajan %${pct} marj öneriyor, onayınızı bekliyor`
        : `Agent suggests ${pct}% margin, waiting for your approval`,
    sentWord: tr ? "gönderildi" : "sent",
    status: {
      draft: tr ? "Taslak" : "Draft",
      estimating: tr ? "Tahmin" : "Estimating",
      waiting_customer: tr ? "Müşteri bekleniyor" : "Waiting for customer",
      ready: tr ? "Hazır" : "Ready",
      sent: tr ? "Gönderildi" : "Sent",
      cancelled: tr ? "İptal" : "Cancelled",
      won: tr ? "Kazanıldı" : "Won",
      lost: tr ? "Kaybedildi" : "Lost",
      produced: tr ? "Üretildi" : "Produced",
    } as Record<string, string>,
  };
}

function money(value: number, locale: string): string {
  const tag = locale === "tr" ? "tr-TR" : "en-US";
  const cents = Math.round(Math.abs(value) * 100) % 100 !== 0;
  return `${value.toLocaleString(tag, {
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: 2,
  })} ₺`;
}

function winPct(rate: number | null): string {
  if (rate == null || !Number.isFinite(rate)) return "—";
  return `${Math.round(rate * 100)}%`;
}

function rfqThumb(rfq: RfqRecord): string | null {
  const line = flattenDemandLines(rfq.lines ?? [])[0];
  if (line) {
    const url = lineThumbUrl(line);
    if (url) return url;
  }
  const stored = (rfq.part_thumbs ?? []).map((url) => url.trim()).find(Boolean);
  return stored ?? null;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return (dot >= 0 ? name.slice(dot + 1) : "file").slice(0, 4).toUpperCase();
}

function readableError(message: string): string {
  const detail = message.match(/"detail"\s*:\s*"([^"]+)"/);
  if (detail) return detail[1];
  return message.replace(/^upload \d+:\s*/, "");
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


export function HomePage({ onNavigate }: Props) {
  const { locale } = useLanguage();
  const shell = useShell();
  const text = copy(locale);
  const tr = locale === "tr";
  const { refresh } = useOrgCatalog();
  const alerts = useAlerts();

  const [rfqs, setRfqs] = useState<RfqRecord[]>([]);
  const [listReady, setListReady] = useState(false);
  const [stats, setStats] = useState<RfqStats | null>(null);
  const [jobCount, setJobCount] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [uploads, setUploads] = useState<UploadRow[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isE2eMockMode()) {
      const mode = e2eOnboardingFixture();
      setJobCount(0);
      if (mode === "fresh" || mode === "partial") {
        setRfqs([]);
        setStats(orgStatsFromRfqs([]));
      } else {
        const rows = buildE2eHomeRfqs();
        const thin =
          typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("home") === "thin";
        const shown = thin ? rows.filter((row) => row.id === "e2e-home-15") : rows;
        setRfqs(shown);
        setStats(orgStatsFromRfqs(shown));
      }
      setListReady(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      const [list, statsBody, jobs] = await Promise.all([
        fetchRfqs(200).then((value) => ({ ok: true as const, value })).catch(() => ({ ok: false as const })),
        fetchRfqStats().catch(() => null),
        listJobs(50).then((value) => ({ ok: true as const, value })).catch(() => ({ ok: false as const })),
      ]);
      if (cancelled) return;
      if (list.ok && Array.isArray(list.value.items)) setRfqs(list.value.items);
      setListReady(true);
      if (isRfqStats(statsBody)) setStats(statsBody);
      setJobCount(jobs.ok ? (Array.isArray(jobs.value) ? jobs.value.length : 0) : null);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const tag = locale === "tr" ? "tr-TR" : "en-GB";
  const today = useMemo(() => {
    try {
      return new Date().toLocaleDateString(tag, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    } catch {
      return "";
    }
  }, [tag]);

  const series = useMemo(() => (listReady ? sentWonSeries(rfqs, { locale }) : []), [listReady, locale, rfqs]);
  const wait = useMemo(() => homeWaiting(rfqs), [rfqs]);
  const firstRun = drawingPresence(listReady ? rfqs.length : null, jobCount) === 0;

  const needle = query.trim().toLowerCase();
  const shownWait = needle
    ? wait.filter((row) => waitHaystack(row, text, locale).includes(needle))
    : wait;
  const recent = useMemo(() => {
    const sorted = [...rfqs].sort(
      (a, b) =>
        (Date.parse(b.updated_at ?? b.created_at ?? "") || 0) -
        (Date.parse(a.updated_at ?? a.created_at ?? "") || 0),
    );
    const filtered = needle
      ? sorted.filter((rfq) =>
          `${rfq.rfq_no} ${rfq.title ?? ""} ${rfq.customer_name ?? ""}`.toLowerCase().includes(needle),
        )
      : sorted;
    return filtered;
  }, [needle, rfqs]);

  function progressOf(id: string) {
    return (fraction: number) =>
      setUploads((prev) => prev.map((row) => (row.id === id ? { ...row, progress: fraction } : row)));
  }

  async function sendFiles(files: File[]) {
    if (files.length === 0) return;
    const batch = files.map((file) => ({
      id: `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      ext: extOf(file.name),
      size: file.size,
      state: "uploading" as const,
      message: text.uploading,
    }));
    setUploads((prev) => [...batch, ...prev].slice(0, 6));
    await Promise.all(
      files.map(async (file, index) => {
        const id = batch[index].id;
        try {
          if (/\.(step|stp|dxf|dwg)$/i.test(file.name)) {
            const pick = { name: file.name, file };
            await getPlatform().submitCadWork([pick], pick, DEFAULT_PARSE_PREFS, tr ? "tr" : "en", {
              onUploadProgress: progressOf(id),
            });
            void refresh();
          } else {
            await uploadMixedFiles([file], progressOf(id));
          }
          setUploads((prev) =>
            prev.map((row) => (row.id === id ? { ...row, state: "done", message: text.uploaded } : row)),
          );
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          logger.info("home_upload_failed", { msg: message });
          setUploads((prev) =>
            prev.map((row) => (row.id === id ? { ...row, state: "error", message: readableError(message) } : row)),
          );
        }
      }),
    );
  }

  function trySample() {
    if (isE2eMockMode()) {
      onNavigate("drawings", "e2e-job-1");
      return;
    }
    void fetchDemoStepFile()
      .then((file) => sendFiles([file]))
      .catch((e) => {
        const message = e instanceof Error ? e.message : String(e);
        logger.info("home_sample_failed", { msg: message });
        setUploads((prev) =>
          [
            {
              id: "sample",
              name: "sample",
              ext: "STEP",
              size: 0,
              state: "error" as const,
              message: readableError(message),
            },
            ...prev,
          ].slice(0, 6),
        );
      });
  }

  if (!listReady) {
    return (
      <div
        data-theme="light"
        className="sc-home -mx-5 -mb-5 h-full min-h-0 flex-1 bg-fixed"
        style={settingsPageStyle}
        data-testid="home"
        data-mode="pending"
      />
    );
  }

  if (firstRun) {
    return (
      <>
        <input
          ref={fileRef}
          type="file"
          className="sr-only"
          accept={ACCEPT}
          multiple
          data-testid="home-file"
          onChange={(e) => {
            const files = e.currentTarget.files ? [...e.currentTarget.files] : [];
            e.currentTarget.value = "";
            void sendFiles(files);
          }}
        />
        <HomeFirstRun
          locale={locale}
          onChooseFiles={() => fileRef.current?.click()}
          onSample={trySample}
          onFiles={(files) => void sendFiles(files)}
          uploads={uploads}
        />
      </>
    );
  }

  return (
    <div
      data-theme="light"
      className={cx(
        "sc-home font-ds -mx-5 -mb-5 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-fixed px-ds-page-x py-3 text-ds-ink",
        "max-[900px]:-mx-3 max-[900px]:-mb-3 max-[900px]:px-3 max-[900px]:py-3",
      )}
      style={settingsPageStyle}
      data-testid="home"
      data-mode="today"
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
        <header className="flex shrink-0 min-w-0 flex-wrap items-center gap-2.5">
          {shell.narrow ? (
            <button
              type="button"
              className={cx(dsControl, "inline-flex size-[38px] shrink-0 items-center justify-center rounded-full bg-ds-glass p-0 text-ds-ink")}
              aria-label={shell.sidebarOpen ? "Close menu" : "Open menu"}
              data-testid="menu"
              onClick={shell.toggleSidebar}
            >
              <img src={sidebarSimple} alt="" className="size-5" />
            </button>
          ) : null}
          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <h1 className="m-0 font-ds text-ds-page font-semibold text-ds-teal" data-testid="page-heading">
              {text.today}
            </h1>
            <p className="m-0 text-ds-body font-normal text-ds-ink-3">{today}</p>
          </div>
          <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
            <ListSearch
              value={query}
              onChange={setQuery}
              placeholder={text.search}
              label={text.search}
              testId="home-search"
              className="w-[min(100%,340px)]"
            />
            <div className="relative">
              <Button
                variant="icon"
                aria-label={text.notifications}
                aria-expanded={alertsOpen}
                data-testid="home-bell"
                onClick={() => setAlertsOpen((v) => !v)}
              >
                <DsIcon svg={bellIcon} className="size-4" />
              </Button>
              {alertsOpen ? (
                <div
                  className="absolute top-12 right-0 z-20 w-72 rounded-ds-card bg-ds-glass-strong p-3 text-ds-ink shadow-ds-card backdrop-blur-ds"
                  role="dialog"
                  data-testid="home-alerts"
                >
                  <p className="m-0 mb-2 text-ds-item font-semibold">{text.waiting}</p>
                  {alerts.length === 0 ? (
                    <p className="m-0 text-ds-body text-ds-ink-3">{text.alertsEmpty}</p>
                  ) : (
                    <ul className="m-0 flex list-none flex-col gap-2 p-0">
                      {alerts.map((alert) => (
                        <li key={alert.key}>
                          <button
                            type="button"
                            className={cx(dsControl, "w-full rounded-ds-pill bg-ds-hover px-3 py-2 text-left text-ds-body font-normal text-ds-ink")}
                            onClick={() => {
                              setAlertsOpen(false);
                              onNavigate(alert.to.page, alert.to.id, alert.to.sub);
                            }}
                          >
                            {alert.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <input
          ref={fileRef}
          type="file"
          className="sr-only"
          accept={ACCEPT}
          multiple
          data-testid="home-file"
          onChange={(e) => {
            const files = e.currentTarget.files ? [...e.currentTarget.files] : [];
            e.currentTarget.value = "";
            void sendFiles(files);
          }}
        />

        {stats || series.length > 0 ? (
          <section
            className={cx(glass, "grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)] min-[960px]:grid-cols-[minmax(220px,360px)_minmax(0,1fr)]")}
            data-testid="home-value"
          >
            {stats ? (
              <WinRate stats={stats} text={text} />
            ) : (
              <div className="min-h-[148px]" data-testid="home-win-pending" />
            )}
            {series.length > 0 ? (
              <div className="flex h-full min-h-0 min-w-0 flex-col border-ds-hair px-5 py-4 max-[959px]:border-t min-[960px]:border-l">
                <FlowChart
                  points={series}
                  title={text.chartTitle}
                  sentLabel={text.sent}
                  wonLabel={text.won}
                  emptyLabel={text.chartEmpty}
                  tip={(point) => text.chartTip(point.label, point.sent, point.won)}
                  locale={locale}
                />
              </div>
            ) : null}
          </section>
        ) : null}

        <div
          className={cx(
            "flex shrink-0 min-w-0 flex-wrap items-center gap-4 rounded-ds-card border border-dashed border-ds-hair bg-ds-glass px-5 py-3",
            dragOver && "border-ds-amber bg-ds-amber-soft/40",
          )}
          data-testid="home-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            void sendFiles([...e.dataTransfer.files]);
          }}
        >
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-[12px] bg-ds-glass-strong text-ds-ink">
            <DsIcon svg={uploadIcon} className="size-[18px]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-ds-item font-semibold text-ds-ink">{text.dropTitle}</span>
            <span className="mt-1 block text-ds-body font-normal text-ds-ink-3">{text.dropHint}</span>
          </span>
          <Button variant="secondary" data-testid="home-sample" onClick={trySample}>
            {text.sample}
          </Button>
          <Button variant="primary" data-testid="home-choose-files" onClick={() => fileRef.current?.click()}>
            <DsIcon svg={uploadBtn} className="size-4 text-ds-on-amber" />
            {text.choose}
          </Button>
        </div>

        {uploads.length > 0 ? <UploadList rows={uploads} text={text} tr={tr} onDismiss={(id) => setUploads((prev) => prev.filter((row) => row.id !== id))} /> : null}

        <div className="grid min-h-0 min-w-0 flex-1 grid-rows-[minmax(0,1fr)] items-stretch gap-3 min-[960px]:grid-cols-[minmax(0,1fr)_minmax(260px,360px)]">
          <section className={cx(glass, "flex h-full min-h-0 flex-col")} data-testid="home-waiting" aria-labelledby="home-wait-h">
            <div className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-2 border-b border-ds-hair px-5 py-2">
              <h2 id="home-wait-h" className="m-0 font-ds text-ds-item font-semibold text-ds-ink">
                {text.waiting}
              </h2>
              <span className="inline-flex items-center rounded-ds-pill bg-ds-glass-strong px-2 py-[3px] font-ds text-ds-badge font-semibold text-ds-ink">
                {shownWait.length}
              </span>
              <span className="ml-auto text-ds-body font-normal text-ds-ink-3">{text.byPriority}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
            {shownWait.length === 0 ? (
              <p className="m-0 px-5 py-8 text-ds-body text-ds-ink-3" data-testid="home-waiting-empty">
                {text.nothing}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col p-0">
                {shownWait.map((row) => (
                  <WaitRow
                    key={row.kind}
                    row={row}
                    text={text}
                    locale={locale}
                    onNavigate={onNavigate}
                  />
                ))}
              </ul>
            )}
            </div>
          </section>

          <section className={cx(glass, "flex h-full min-h-0 flex-col")} data-testid="home-recent" aria-labelledby="home-recent-h">
            <div className="flex min-h-[52px] shrink-0 flex-wrap items-center gap-2 border-b border-ds-hair px-5 py-2">
              <h2 id="home-recent-h" className="m-0 flex-1 font-ds text-ds-item font-semibold text-ds-ink">
                {text.recent}
              </h2>
              <Button variant="link" data-testid="home-all-rfqs" onClick={() => onNavigate("rfq")}>
                {text.allRfqs}
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
            {recent.length === 0 ? (
              <p className="m-0 px-5 py-8 text-ds-body text-ds-ink-3" data-testid="home-recent-empty">
                {text.recentEmpty}
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col p-0">
                {recent.map((rfq) => {
                  const progress = homeRowProgress(rfq);
                  const shown = rfqDisplayStatus(rfq);
                  return (
                    <li key={rfq.id} className="border-b border-ds-hair last:border-b-0" data-testid={`home-rfq-${rfq.rfq_no}`}>
                      <button
                        type="button"
                        className={cx(dsControl, "flex w-full items-start gap-3 bg-transparent px-5 py-3 text-left")}
                        onClick={() => onNavigate("rfq", rfq.id)}
                      >
                        <Thumb src={rfqThumb(rfq)} alt="" className={thumbFrame} />
                        <span className="flex min-w-0 flex-1 flex-col gap-2">
                          <span className="flex items-center gap-2">
                            <Truncate
                              text={rfq.title || rfq.customer_name || rfq.rfq_no}
                              className="flex-1 text-ds-item font-semibold text-ds-ink"
                            />
                            <span className="shrink-0 text-ds-body font-normal text-ds-ink-3">
                              {homeAgeLabel(rfq.updated_at ?? rfq.created_at, { locale }) ?? ""}
                            </span>
                          </span>
                          <span className="flex items-center gap-3">
                            <span className="shrink-0 text-ds-body font-normal text-ds-ink-3">{rfq.rfq_no}</span>
                            <span className="flex gap-0.5" aria-hidden="true">
                              {progress.steps.map((state, index) => (
                                <span
                                  key={index}
                                  className={cx(
                                    "block h-1 w-5 rounded-full",
                                    state === "done" ? "bg-ds-amber" : "bg-ds-hover",
                                  )}
                                />
                              ))}
                            </span>
                          </span>
                          <RfqStatus status={shown} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function waitHaystack(row: HomeWaitItem, text: ReturnType<typeof copy>, locale: string): string {
  const view = waitView(row, text, locale);
  return `${view.label} ${view.title} ${view.detail}`.toLowerCase();
}

function waitView(row: HomeWaitItem, text: ReturnType<typeof copy>, locale: string): {
  label: string;
  title: string;
  detail: string;
  action: string;
  step: string | null;
  accent: boolean;
  tone: "danger" | "warn" | "ok";
} {
  const rfq = row.rfq;
  const name = rfq.title || "";
  if (row.kind === "missing") {
    return {
      label: text.missing,
      title: text.missingTitle(row.missing ?? 0),
      detail: [rfq.rfq_no, name].filter(Boolean).join(", "),
      action: text.match,
      step: "supply",
      accent: false,
      tone: "danger",
    };
  }
  if (row.kind === "customer") {
    const when = homeSentWhen(rfq.waiting_at ?? rfq.sent_at, { locale });
    const sent = when ? `${text.sentWord} ${when}` : "";
    return {
      label: text.customer,
      title: text.customerTitle(rfq.rfq_no, row.questions ?? null),
      detail: [rfq.customer_name, sent].filter(Boolean).join(" · "),
      action: text.pasteReply,
      step: null,
      accent: false,
      tone: "warn",
    };
  }
  if (row.kind === "followup") {
    const total = typeof rfq.quote_total_try === "number" ? money(rfq.quote_total_try, locale) : "";
    const right = [name, total].filter(Boolean).join(", ");
    return {
      label: text.followUp,
      title: text.followTitle(rfq.rfq_no, row.days ?? 0),
      detail: [rfq.customer_name, right].filter(Boolean).join(" · "),
      action: text.followAction,
      step: "quote",
      accent: false,
      tone: "warn",
    };
  }
  const total = typeof rfq.quote_total_try === "number" ? money(rfq.quote_total_try, locale) : "";
  return {
    label: text.margin,
    title: text.marginTitle(row.marginPct ?? 0),
    detail: [[rfq.rfq_no, name].filter(Boolean).join(", "), total].filter(Boolean).join(" · "),
    action: text.reviewMargin,
    step: "margin",
    accent: true,
    tone: "ok",
  };
}

function WaitRow({
  row,
  text,
  locale,
  onNavigate,
}: {
  row: HomeWaitItem;
  text: ReturnType<typeof copy>;
  locale: string;
  onNavigate: Props["onNavigate"];
}) {
  const view = waitView(row, text, locale);
  return (
    <li
      className="flex min-w-0 flex-wrap items-center gap-3 border-b border-ds-hair px-5 py-3.5 last:border-b-0"
      data-testid={`home-wait-${row.kind}`}
    >
      <span className="inline-flex w-24 shrink-0 items-center gap-2 text-ds-body">
        <span
          className={cx(
            "size-1.5 rounded-full",
            view.tone === "danger" && "bg-ds-coral",
            view.tone === "warn" && "bg-warning",
            view.tone === "ok" && "bg-ds-teal",
          )}
        />
        <span className={view.tone === "danger" ? "text-ds-coral" : "text-ds-ink"}>{view.label}</span>
      </span>
      <Thumb src={rfqThumb(row.rfq)} alt="" className={thumbFrame} />
      <span className="min-w-[160px] flex-1">
        <Truncate text={view.title} className="text-ds-item font-semibold text-ds-ink" />
        <Truncate text={view.detail} className="text-ds-body font-normal text-ds-ink-3" />
      </span>
      <button
        type="button"
        className={cx(
          dsControl,
          "inline-flex h-[34px] shrink-0 items-center justify-center rounded-ds-pill px-3.5 py-0 text-ds-body font-semibold",
          view.accent
            ? "bg-ds-amber text-ds-on-amber shadow-ds-amber"
            : "bg-ds-glass-strong text-ds-ink",
        )}
        data-testid={`home-wait-action-${row.kind}`}
        onClick={() => onNavigate("rfq", row.rfq.id, view.step)}
      >
        {view.action}
      </button>
    </li>
  );
}

function WinRate({ stats, text }: { stats: RfqStats; text: ReturnType<typeof copy> }) {
  const decided = stats.won + stats.lost;
  const blocks = [
    { id: "home-win-won", label: text.won, value: stats.won, dot: "bg-ds-teal" },
    { id: "home-win-lost", label: text.lost, value: stats.lost, dot: "bg-danger" },
    { id: "home-win-open", label: text.open, value: stats.open, dot: "bg-ds-ink-3" },
  ];
  return (
    <div className="flex h-full flex-col p-5" data-testid="home-win-rate">
      <h2 className="m-0 font-ds text-ds-item font-semibold text-ds-ink">{text.winRate}</h2>
      <p className="m-0 mt-3 font-ds text-[40px] leading-none font-semibold tracking-[-0.6px] text-ds-ink" data-testid="home-win-value">
        {winPct(stats.win_rate)}
      </p>
      <p className="m-0 mt-3 max-w-[280px] text-ds-body font-normal text-ds-ink-3" data-testid="home-win-note">
        {text.decided(stats.won, decided)}
      </p>
      <div className="mt-auto flex flex-col gap-3 pt-4">
        {decided > 0 ? (
          <div className="flex h-3.5 gap-0.5 overflow-hidden rounded-full" data-testid="home-win-bar">
            {stats.won > 0 ? <span className="h-full bg-ds-teal" style={{ flex: `${stats.won} 1 0` }} /> : null}
            {stats.lost > 0 ? <span className="h-full bg-danger" style={{ flex: `${stats.lost} 1 0` }} /> : null}
          </div>
        ) : null}
        <div className="grid grid-cols-3 gap-2">
          {blocks.map((block) => (
            <div key={block.id} className="rounded-[14px] bg-ds-glass-strong px-2.5 py-2.5" data-testid={block.id}>
              <span className="flex items-center gap-1.5 text-ds-badge text-ds-ink-3">
                <span className={cx("size-1.5 rounded-full", block.dot)} />
                {block.label}
              </span>
              <span className="mt-1 block font-ds text-[22px] leading-none font-semibold text-ds-ink">{block.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Swatch({ label, amber }: { label: string; amber?: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-ds-body font-normal text-ds-ink-3">
      <span className={cx("inline-block h-2.5 w-2.5 rounded-[3px]", amber ? "bg-ds-teal" : "bg-ds-teal/40")} />
      {label}
    </span>
  );
}

/** Fold the single-day "Today" bucket into the week it belongs to. Counts are unchanged. */
function fourWeeks(points: SentWonPoint[]): SentWonPoint[] {
  if (points.length <= 4) return points;
  const head = points.slice(0, 3);
  const tail = points.slice(3);
  return [
    ...head,
    {
      label: tail[0].label,
      sent: tail.reduce((sum, point) => sum + point.sent, 0),
      won: tail.reduce((sum, point) => sum + point.won, 0),
    },
  ];
}

function scaleTicks(max: number): number[] {
  if (max <= 0) return [];
  if (max <= 3) return Array.from({ length: max }, (_, i) => i + 1);
  const mid = Math.round(max / 2);
  const low = Math.max(1, Math.round(max / 3));
  return [low, mid, max].filter((value, index, all) => all.indexOf(value) === index).slice(0, 3);
}

function barHeight(value: number, max: number): string {
  if (value <= 0 || max <= 0) return "0px";
  return `max(12px, ${(value / max) * 78}%)`;
}

function FlowChart({
  points,
  title,
  sentLabel,
  wonLabel,
  emptyLabel,
  tip,
  locale,
}: {
  points: SentWonPoint[];
  title: string;
  sentLabel: string;
  wonLabel: string;
  emptyLabel: string;
  tip: (point: SentWonPoint) => string;
  locale: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const weeks = fourWeeks(points);
  const sentTotal = weeks.reduce((sum, point) => sum + point.sent, 0);
  const wonTotal = weeks.reduce((sum, point) => sum + point.won, 0);
  const empty = sentTotal === 0 && wonTotal === 0;
  const max = Math.max(0, ...weeks.map((point) => Math.max(point.sent, point.won)));
  const ticks = scaleTicks(max);
  const tag = locale === "tr" ? "tr-TR" : "en-US";
  const described = weeks.map((point) => tip(point).replace("\n", ", ")).join("; ");
  const active = hover != null ? weeks[hover] : null;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-testid="home-chart" role="group" aria-label={described}>
      <div className="flex shrink-0 flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="m-0 font-ds text-ds-body font-medium text-ds-ink-3">{title}</h2>
          <p className="m-0 mt-1 flex items-baseline gap-5">
            <span className="inline-flex items-baseline gap-1.5">
              <span className="font-ds text-[32px] leading-none font-semibold tracking-[-0.4px] text-ds-ink" data-testid="home-chart-sent">
                {sentTotal.toLocaleString(tag)}
              </span>
              <span className="text-ds-body text-ds-ink-3">{sentLabel}</span>
            </span>
            <span className="inline-flex items-baseline gap-1.5">
              <span className="font-ds text-[32px] leading-none font-semibold tracking-[-0.4px] text-ds-ink" data-testid="home-chart-won">
                {wonTotal.toLocaleString(tag)}
              </span>
              <span className="text-ds-body text-ds-ink-3">{wonLabel}</span>
            </span>
          </p>
          {empty ? (
            <p className="m-0 mt-1 text-ds-body text-ds-ink-3" data-testid="home-chart-empty">
              {emptyLabel}
            </p>
          ) : null}
        </div>
        <span className="flex items-center gap-3 pb-1">
          <Swatch label={sentLabel} />
          <Swatch label={wonLabel} amber />
        </span>
      </div>
      <div className="flex min-h-[140px] min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 gap-1">
          <div className="relative w-5 shrink-0" aria-hidden="true">
            {ticks.map((tick) => (
              <span
                key={tick}
                className="absolute right-0 -translate-y-1/2 text-[10px] leading-none text-ds-ink-3"
                style={{ bottom: `${(tick / max) * 78}%` }}
              >
                {tick}
              </span>
            ))}
          </div>
          <div className="relative flex min-w-0 flex-1 gap-2">
            {ticks.map((tick) => (
              <span
                key={tick}
                className="pointer-events-none absolute right-0 left-0 border-t border-ds-ink/10"
                style={{ bottom: `${(tick / max) * 78}%` }}
                aria-hidden="true"
              />
            ))}
            {weeks.map((point, index) => {
              const last = index === weeks.length - 1;
              return (
                <div
                  key={point.label}
                  className="relative flex h-full min-w-0 flex-1 items-end justify-center outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ds-ink"
                  tabIndex={0}
                  onMouseEnter={() => setHover(index)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(index)}
                  onBlur={() => setHover(null)}
                >
                  {hover === index && active ? (
                    <div
                      className={cx(
                        "pointer-events-none absolute top-1 z-10 rounded-[12px] bg-ds-ink px-2.5 py-1.5 text-ds-body leading-4 whitespace-pre text-white",
                        index === 0 ? "left-0" : last ? "right-0" : "left-1/2 -translate-x-1/2",
                      )}
                      data-testid="home-chart-tip"
                    >
                      {tip(active)}
                    </div>
                  ) : null}
                  <div className="relative flex h-full w-[70%] items-end justify-center">
                    <div className="absolute inset-0 rounded-[22px] bg-ds-ink/[0.05]" aria-hidden="true" />
                    <div className="relative flex h-full w-full items-end justify-center gap-px px-1.5">
                      <WeekBar value={point.sent} max={max} tone="sent" />
                      <WeekBar value={point.won} max={max} tone="won" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-2 flex gap-2 pl-6">
          {weeks.map((point, index) => (
            <span
              key={point.label}
              className={cx(
                "min-w-0 flex-1 truncate text-center text-ds-badge font-normal text-ds-ink-3",
                index === weeks.length - 1 && "font-semibold text-ds-ink",
              )}
            >
              {point.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function WeekBar({ value, max, tone }: { value: number; max: number; tone: "sent" | "won" }) {
  return (
    <div className="flex h-full w-12 max-w-[48%] flex-col justify-end">
      <span
        className={cx(
          "mb-1 text-center font-ds text-[13px] leading-none font-semibold",
          value > 0 ? "text-ds-ink" : "text-ds-ink-3",
        )}
      >
        {value}
      </span>
      <div
        className={cx("w-full rounded-t-[12px]", tone === "won" ? "bg-ds-teal" : "bg-ds-teal/40")}
        style={{ height: barHeight(value, max) }}
      />
    </div>
  );
}

function UploadList({
  rows,
  text,
  tr,
  onDismiss,
}: {
  rows: UploadRow[];
  text: ReturnType<typeof copy>;
  tr: boolean;
  onDismiss: (id: string) => void;
}) {
  return (
    <div data-testid="home-uploads">
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="m-0 text-ds-item font-semibold text-ds-ink">
          {rows.some((row) => row.state === "uploading")
            ? `${text.uploading} ${rows.length} ${tr ? "dosya" : rows.length === 1 ? "file" : "files"}`
            : text.filesReceived}
        </p>
        {rows.every((row) => row.state !== "uploading") ? (
          <p className="m-0 text-ds-body font-normal text-ds-ink-3">
            {[
              rows.some((row) => row.state === "done")
                ? `${rows.filter((row) => row.state === "done").length} ${text.uploaded.toLowerCase()}`
                : null,
              rows.some((row) => row.state === "error")
                ? `${rows.filter((row) => row.state === "error").length} ${text.couldNotRead}`
                : null,
            ]
              .filter(Boolean)
              .join(", ")}
          </p>
        ) : null}
      </div>
      <ul className="m-0 flex list-none flex-wrap gap-3 p-0">
        {rows.map((row) => {
          const size = formatBytes(row.size);
          const pct = Math.round((row.progress ?? 0) * 100);
          const sending = row.state === "uploading" && (row.progress ?? 0) < 1;
          const detail =
            row.state === "uploading"
              ? sending
                ? [text.uploading, `${pct}%`, size].filter(Boolean).join(", ")
                : text.processing
              : row.state === "done"
                ? text.uploaded
                : row.message;
          return (
            <li
              key={row.id}
              className={cx(
                "flex w-[280px] max-w-full items-start gap-3 rounded-ds-card bg-ds-glass px-3 py-2.5 shadow-ds-card",
                row.state === "error" && "outline outline-1 outline-ds-coral",
              )}
              data-state={row.state}
            >
              {row.state === "uploading" ? (
                <CircularProgress value={sending ? pct : null} size={32} thickness={3} aria-label={sending ? text.uploading : text.processing} className="shrink-0">
                  <CircularProgressIndicator>
                    <CircularProgressTrack />
                    <CircularProgressRange />
                  </CircularProgressIndicator>
                </CircularProgress>
              ) : (
                <span className="inline-flex h-8 min-w-8 shrink-0 items-center justify-center rounded-ds-pill bg-ds-glass-strong px-1 text-ds-badge font-medium text-ds-ink-3">
                  {row.ext}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ds-body font-medium text-ds-ink">{row.name}</span>
                {row.state !== "error" ? (
                  <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-ds-hover">
                    <span
                      className={cx("block h-full bg-ds-amber", row.state === "uploading" && !sending && "animate-pulse")}
                      style={{ width: row.state === "uploading" ? `${pct}%` : "100%" }}
                      data-testid="home-upload-bar"
                    />
                  </span>
                ) : null}
                <span className={cx("mt-1 block text-ds-badge", row.state === "error" ? "text-ds-coral" : "text-ds-ink-3")}>{detail}</span>
              </span>
              <button
                type="button"
                className={cx(dsControl, "bg-transparent px-1 py-0 text-lg leading-none text-ds-ink-3")}
                aria-label="Dismiss"
                onClick={() => onDismiss(row.id)}
              >
                ×
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
